import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

const UserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const currentRole = String(localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();

  const isSuperAdmin = currentRole === "superadmin";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState([]);

  const [businessId, setBusinessId] = useState("");
  const [businesses, setBusinesses] = useState([]);

  const [status, setStatus] = useState("active");

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const [isFetchingRoles, setIsFetchingRoles] = useState(!isSuperAdmin);

  const [isFetchingBusinesses, setIsFetchingBusinesses] =
    useState(isSuperAdmin);
  const [supplierId, setSupplierId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [isFetchingSuppliers, setIsFetchingSuppliers] = useState(false);
  const [supplierFieldError, setSupplierFieldError] = useState("");

  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
  const [customerFieldError, setCustomerFieldError] = useState("");

  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchBusinesses = async () => {
      setIsFetchingBusinesses(true);

      try {
        const currentUserId = localStorage.getItem("id");

        const payload = {
          action: "get-all-business",
          currentUserId,
          page: 1,
          limit: 100,
          status: "active",
        };

        const response = await axios.post(API_URL, payload);

        let responseData = response.data;

        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true && responseData.data) {
          const list = responseData.data.businesses || [];

          setBusinesses(list);
        } else {
          toast.error(responseData.message || "Failed to fetch businesses", {
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Fetch businesses error:", error);

        toast.error("Failed to load businesses", {
          autoClose: 2000,
        });
      } finally {
        setIsFetchingBusinesses(false);
      }
    };

    fetchBusinesses();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin || role !== "supplier") {
      setSuppliers([]);
      setSupplierId("");
      setSupplierFieldError("");
      return;
    }

    const fetchSuppliers = async () => {
      setIsFetchingSuppliers(true);
      setSupplierFieldError("");

      try {
        const currentUserId = localStorage.getItem("id");
        const userRole = localStorage.getItem("role");

        const payload = {
          action: "get-suppliers",
          status: "active",
          currentUserId,
          page: 1,
          limit: 500,
        };
        if (userRole !== "admin" && userRole !== "superadmin") {
          payload.status = "active";
        }

        const response = await axios.post(API_URL, payload);

        let responseData = response.data;

        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true) {
          const list =
            responseData.suppliers || responseData.data?.suppliers || [];
          setSuppliers(Array.isArray(list) ? list : []);
          setSupplierFieldError("");
        } else {
          setSuppliers([]);
          setSupplierFieldError(
            responseData.message ||
              "Supplier ID is required. Please select or update the supplier.",
          );
        }
      } catch (error) {
        console.error("Fetch suppliers error:", error);
        setSuppliers([]);
        const errMsg =
          error.response?.data?.message ||
          (typeof error.response?.data === "string"
            ? error.response.data
            : null) ||
          "Failed to load suppliers. Please update the supplier ID.";
        setSupplierFieldError(errMsg);
      } finally {
        setIsFetchingSuppliers(false);
      }
    };

    fetchSuppliers();
  }, [isSuperAdmin, role]);

  useEffect(() => {
    if (isSuperAdmin) return;

    const fetchRoles = async () => {
      setIsFetchingRoles(true);

      try {
        const payload = {
          action: "get-role",
          page: 1,
          limit: 100,
        };

        const response = await axios.post(API_URL, payload);

        let responseData = response.data;

        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true && responseData.data) {
          const activeRoles = responseData.data.filter(
            (r) => r.status === "active",
          );

          setRoles(activeRoles);
        } else {
          toast.error(responseData.message || "Failed to fetch roles", {
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Fetch roles error:", error);

        toast.error("Failed to load roles", {
          autoClose: 2000,
        });
      } finally {
        setIsFetchingRoles(false);
      }
    };

    fetchRoles();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!id) {
      toast.error("User ID is missing", {
        autoClose: 2000,
      });

      navigate("/user");
      return;
    }

    fetchUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isSuperAdmin || role !== "customer") {
      setCustomers([]);
      setCustomerId("");
      setCustomerFieldError("");
      return;
    }

    const fetchCustomers = async () => {
      setIsFetchingCustomers(true);
      setCustomerFieldError("");

      try {
        const currentUserId = localStorage.getItem("id");
        const userRole = localStorage.getItem("role");

        const payload = {
          action: "get-all-customers",
          currentUserId,
          page: 1,
          limit: 500,
        };
        if (userRole !== "admin" && userRole !== "superadmin") {
          payload.status = "active";
        }

        const response = await axios.post(API_URL, payload);

        let responseData = response.data;

        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true) {
          const list =
            responseData.customers || responseData.data?.customers || [];
          setCustomers(Array.isArray(list) ? list : []);
          setCustomerFieldError("");
        } else {
          setCustomers([]);
          setCustomerFieldError(
            responseData.message ||
              "Customer ID is required. Please select or update the customer.",
          );
        }
      } catch (error) {
        console.error("Fetch customers error:", error);
        setCustomers([]);
        const errMsg =
          error.response?.data?.message ||
          (typeof error.response?.data === "string"
            ? error.response.data
            : null) ||
          "Failed to load customers. Please update the customer ID.";
        setCustomerFieldError(errMsg);
      } finally {
        setIsFetchingCustomers(false);
      }
    };

    fetchCustomers();
  }, [isSuperAdmin, role]);

  const fetchUser = async () => {
    setIsFetching(true);

    try {
      const payload = {
        action: "get-user",
        id: id,
      };

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;

      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (responseData.success === true && responseData.user) {
        const user = responseData.user;

        setName(user.name || "");
        setEmail(user.email || "");
        setPhoneNumber(user.phoneNumber || "");
        setStatus(user.status || "active");

        if (isSuperAdmin) {
          setRole("admin");

          if (user.businessId) {
            const existingBusinessId =
              typeof user.businessId === "object"
                ? user.businessId._id
                : user.businessId;

            setBusinessId(existingBusinessId || "");
          }
        } else {
          const userRole = user.role || "";

          setRole(userRole);

          if (userRole.toLowerCase() === "supplier") {
            const existingSupplierId =
              typeof user.supplierId === "object"
                ? user.supplierId?._id
                : user.supplierId;

            setSupplierId(existingSupplierId || "");
            setCustomerId("");
          } else if (userRole.toLowerCase() === "customer") {
            const existingCustomerId =
              typeof user.customerId === "object"
                ? user.customerId?._id
                : user.customerId;

            setCustomerId(existingCustomerId || "");
            setSupplierId("");
          } else {
            setSupplierId("");
            setCustomerId("");
          }
        }
      } else {
        toast.error(responseData.message || "Failed to fetch user details", {
          autoClose: 2000,
        });

        navigate("/user");
      }
    } catch (error) {
      console.error("Fetch user error:", error);

      if (error.response && error.response.data) {
        let errorData = error.response.data;

        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {}
        }

        toast.error(
          errorData.message || "An error occurred while fetching user details",
          {
            autoClose: 2000,
          },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          {
            autoClose: 2000,
          },
        );
      }

      navigate("/user");
    } finally {
      setIsFetching(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);

    setPhoneNumber(value);
  };

  const validateForm = () => {
    if (!name.trim()) {
      toast.error("Please enter a name", {
        autoClose: 2000,
      });

      return false;
    }

    if (!email.trim()) {
      toast.error("Please enter an email", {
        autoClose: 2000,
      });

      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address", {
        autoClose: 2000,
      });

      return false;
    }

    if (!phoneNumber.trim()) {
      toast.error("Please enter a phone number", {
        autoClose: 2000,
      });

      return false;
    }

    if (phoneNumber.length !== 10) {
      toast.error("Phone number must be exactly 10 digits", {
        autoClose: 2000,
      });

      return false;
    }

    if (isSuperAdmin && !businessId) {
      toast.error("Please select a business", {
        autoClose: 2000,
      });

      return false;
    }

    if (!isSuperAdmin && (!role || role.trim() === "")) {
      toast.error("Please select a role", {
        autoClose: 2000,
      });

      return false;
    }
    if (!isSuperAdmin && role.toLowerCase() === "supplier" && !supplierId) {
      toast.error("Please select a supplier", {
        autoClose: 2000,
      });

      return false;
    }

    if (!isSuperAdmin && role.toLowerCase() === "customer" && !customerId) {
      toast.error("Please select a customer", {
        autoClose: 2000,
      });

      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const currentUserId = localStorage.getItem("id");

    if (!currentUserId) {
      toast.error("User session expired. Please login again", {
        autoClose: 2000,
      });

      navigate("/");
      return;
    }

    setIsLoading(true);

    try {
      const payload = isSuperAdmin
        ? {
            action: "update-user",
            id: id,
            currentUserId,
            name: name.trim(),
            email: email.trim(),
            phoneNumber: phoneNumber.trim(),
            role: "admin",
            status,
            businessId,
          }
        : {
            action: "update-user",
            id: id,
            currentUserId,
            name: name.trim(),
            email: email.trim(),
            phoneNumber: phoneNumber.trim(),
            role,
            status,
            supplierId: role.toLowerCase() === "supplier" ? supplierId : null,
            customerId: role.toLowerCase() === "customer" ? customerId : null,
          };

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;

      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (responseData.success === true) {
        toast.success(responseData.message || "User updated successfully", {
          autoClose: 2000,
        });

        setTimeout(() => {
          navigate("/user");
        }, 1500);
      } else {
        toast.error(responseData.message || "Failed to update user", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Update user error:", error);

      if (error.response && error.response.data) {
        let errorData = error.response.data;

        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Keep original error
          }
        }

        toast.error(
          errorData.message || "An error occurred while updating user",
          {
            autoClose: 2000,
          },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          {
            autoClose: 2000,
          },
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  /*
   * ============================================================
   * LOADING
   * ============================================================
   */
  if (isFetching) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">
                  {isSuperAdmin ? "Edit Tenant Admin" : "Edit User"}
                </h1>
              </div>

              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/user">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">
                {isSuperAdmin ? "Edit Tenant Admin" : "Edit User"}
              </h1>
            </div>

            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/user">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Name
                          <span className="f_red">*</span>
                        </label>

                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            disabled={isLoading}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Email
                          <span className="f_red">*</span>
                        </label>

                        <div className="col-12 col-md-8">
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={isLoading}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Phone Number
                          <span className="f_red">*</span>
                        </label>

                        <div className="col-12 col-md-8">
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Phone Number"
                            value={phoneNumber}
                            onChange={handlePhoneChange}
                            onKeyPress={(e) => {
                              if (
                                !/[0-9]/.test(e.key) &&
                                e.key !== "Backspace" &&
                                e.key !== "Delete" &&
                                e.key !== "Tab"
                              ) {
                                e.preventDefault();
                              }
                            }}
                            maxLength={10}
                            inputMode="numeric"
                            required
                            disabled={isLoading}
                          />
                        </div>
                      </div>
                      {isSuperAdmin ? (
                        <>
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Business
                              <span className="f_red">*</span>
                            </label>

                            <div className="col-12 col-md-8">
                              <select
                                className="form-control"
                                value={businessId}
                                onChange={(e) => setBusinessId(e.target.value)}
                                disabled={isLoading || isFetchingBusinesses}
                                required
                              >
                                <option value="">Select Business</option>

                                {businesses.map((b) => (
                                  <option key={b._id} value={b._id}>
                                    {b.businessName}
                                  </option>
                                ))}
                              </select>

                              <small className="form-text text-muted">
                                This user will be assigned as the Admin for the
                                selected business.
                              </small>
                            </div>
                          </div>
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Role
                            </label>

                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                className="form-control"
                                value="Admin"
                                readOnly
                                disabled
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Role
                            <span className="f_red">*</span>
                          </label>

                          <div className="col-12 col-md-8">
                            <select
                              className="form-control"
                              value={role}
                              onChange={(e) => setRole(e.target.value)}
                              disabled={isLoading || isFetchingRoles}
                              required
                            >
                              <option value="">Select Role</option>

                              {roles.map((roleItem) => (
                                <option key={roleItem.id} value={roleItem.role}>
                                  {roleItem.role.charAt(0).toUpperCase() +
                                    roleItem.role.slice(1)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      {!isSuperAdmin && role.toLowerCase() === "supplier" && (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Supplier
                            <span className="f_red">*</span>
                          </label>

                          <div className="col-12 col-md-8">
                            <select
                              className={`form-control${
                                supplierFieldError ? " border-danger" : ""
                              }`}
                              value={supplierId}
                              onChange={(e) => {
                                setSupplierId(e.target.value);
                                if (e.target.value) setSupplierFieldError("");
                              }}
                              disabled={isLoading || isFetchingSuppliers}
                              required
                            >
                              <option value="">
                                {isFetchingSuppliers
                                  ? "Loading suppliers..."
                                  : "Select Supplier"}
                              </option>

                              {suppliers.map((s) => {
                                const sid = s.id || s._id || "";
                                const label =
                                  s.clientname || s.clientName || s.name || sid;
                                return (
                                  <option key={sid} value={sid}>
                                    {label}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>
                      )}
                      {!isSuperAdmin && role.toLowerCase() === "customer" && (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Customer
                            <span className="f_red">*</span>
                          </label>

                          <div className="col-12 col-md-8">
                            <select
                              className={`form-control${
                                customerFieldError ? " border-danger" : ""
                              }`}
                              value={customerId}
                              onChange={(e) => {
                                setCustomerId(e.target.value);
                                if (e.target.value) setCustomerFieldError("");
                              }}
                              disabled={isLoading || isFetchingCustomers}
                              required
                            >
                              <option value="">
                                {isFetchingCustomers
                                  ? "Loading customers..."
                                  : "Select Customer"}
                              </option>

                              {customers.map((c) => {
                                const cid = c.id || c._id || "";
                                const label =
                                  c.customerName ||
                                  c.clientname ||
                                  c.name ||
                                  cid;
                                return (
                                  <option key={cid} value={cid}>
                                    {label}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>
                      )}
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Status
                        </label>

                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            disabled={isLoading}
                          >
                            <option value="active">Active</option>

                            <option value="inactive">Inactive</option>
                          </select>
                          {(supplierFieldError || customerFieldError) && (
                            <small className="form-text text-danger d-block mt-1">
                              {supplierFieldError || customerFieldError}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <PulseLoader color="#ffffff" size={8} />
                        ) : (
                          "Update"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserEdit;
